/**
 * AmenitiesPicker — multi-select grouped by category. Reports selected keys to parent.
 */
import React, { useEffect, useState, useMemo } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, Check } from 'lucide-react';
import InfoTip from './InfoTip';

const CATEGORY_LABELS = {
  wellness: 'Wellness & Recreation',
  mobility: 'Mobility & Parking',
  smart: 'Smart / Tech',
  sustainability: 'Sustainability',
  lifestyle: 'Lifestyle',
  premium: 'Concierge / Premium',
  industrial: 'Industrial Add-ons',
};

const CATEGORY_COLORS = {
  wellness: 'bg-rose-50 border-rose-200 text-rose-900',
  mobility: 'bg-blue-50 border-blue-200 text-blue-900',
  smart: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  sustainability: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  lifestyle: 'bg-amber-50 border-amber-200 text-amber-900',
  premium: 'bg-purple-50 border-purple-200 text-purple-900',
  industrial: 'bg-slate-100 border-slate-300 text-slate-900',
};

const formatCost = (a) => {
  const parts = [];
  if (a.fixed_usd) parts.push(`$${(a.fixed_usd / 1000).toFixed(0)}k`);
  if (a.uplift_pct) parts.push(`+${(a.uplift_pct * 100).toFixed(1)}%`);
  if (a.uplift_per_m2) parts.push(`+$${a.uplift_per_m2}/m²`);
  return parts.join(' · ') || 'fixed';
};

export default function AmenitiesPicker({ selected = [], onChange, sector }) {
  const [byCategory, setByCategory] = useState({});
  const [openCats, setOpenCats] = useState(() => new Set(['wellness', 'mobility', 'sustainability']));

  useEffect(() => {
    ConstructionAPI.getAmenities(sector).then(d => setByCategory(d.by_category || {}));
  }, [sector]);

  const toggle = (key) => {
    if (selected.includes(key)) onChange(selected.filter(k => k !== key));
    else onChange([...selected, key]);
  };
  const toggleCat = (cat) => {
    const next = new Set(openCats);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    setOpenCats(next);
  };

  const selCount = selected.length;
  const allCats = useMemo(() => Object.keys(byCategory), [byCategory]);

  return (
    <Card data-testid="amenities-picker">
      <CardHeader className="pb-3">
        <CardTitle className="text-base inline-flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-orange-500" />
          Amenities
          <InfoTip>Optional add-ons. Each amenity adds a fixed cost, a percentage uplift on direct cost, or a per-m² uplift on GFA.</InfoTip>
          {selCount > 0 && <Badge className="ml-1 bg-orange-600 text-white text-xs">{selCount} selected</Badge>}
        </CardTitle>
        <p className="text-xs text-slate-500">Tap an amenity to add it to your project. Costs roll into the build-up automatically.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {allCats.length === 0 && <p className="text-sm text-slate-500">No amenities applicable for this sector.</p>}
        {allCats.map(cat => {
          const items = byCategory[cat] || [];
          const isOpen = openCats.has(cat);
          const selInCat = items.filter(a => selected.includes(a.key)).length;
          return (
            <div key={cat} className={`rounded-lg border ${CATEGORY_COLORS[cat] || 'bg-slate-50 border-slate-200'}`}>
              <button
                onClick={() => toggleCat(cat)}
                className="w-full text-left px-3 py-2 flex items-center justify-between"
                data-testid={`amenities-cat-${cat}`}
              >
                <span className="text-sm font-semibold">{CATEGORY_LABELS[cat] || cat} <span className="text-xs font-normal opacity-60">({items.length})</span></span>
                <span className="text-xs">{selInCat > 0 ? `${selInCat} on` : ''} {isOpen ? '▾' : '▸'}</span>
              </button>
              {isOpen && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 p-2 pt-0">
                  {items.map(a => {
                    const checked = selected.includes(a.key);
                    return (
                      <button
                        key={a.key}
                        onClick={() => toggle(a.key)}
                        className={`text-left px-2.5 py-2 rounded-md border text-xs transition-colors ${checked ? 'bg-orange-600 border-orange-700 text-white' : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-800'}`}
                        data-testid={`amenity-${a.key}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-medium leading-tight">{a.label}</span>
                          {checked && <Check className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
                        </div>
                        <div className={`mt-0.5 text-[10px] ${checked ? 'text-orange-100' : 'text-slate-500'}`}>{formatCost(a)}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
